import { ArrowLeft } from "lucide-react";
import { FadeIn } from "@/components/Reveal";
import { SITE } from "@/config/site";

const UPDATED = "September 2026";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    intro:
      "TSM Group CLE respects the confidentiality of the information businesses share with us. This policy explains what information we collect through this website, how we use it, and the choices available to you.",
    sections: [
      {
        h: "Information We Collect",
        ps: [
          "When you complete our service request form or contact us by phone or email, we collect the information you provide, such as your name, business name, email address, phone number, the service you are requesting, your preferred service date and location, and a brief description of your request.",
          "We also collect standard technical information when you visit this website, such as browser type and pages visited, which helps us understand how the site is used.",
        ],
      },
      {
        h: "How We Use Information",
        ps: [
          "We use the information you provide to review, respond to, schedule, and complete the services you request; to communicate with you about your request; to maintain internal business records; and to meet applicable legal and operational requirements.",
          "We do not sell your personal information, and we do not add information submitted through this website to marketing lists without your permission.",
        ],
      },
      {
        h: "How Information Is Shared",
        ps: [
          "Information is shared within TSM Group CLE only as needed to review and complete your request. Where a service requires coordination with a third party (for example, a testing or screening provider), information is shared only as necessary to deliver that service.",
          "We may also disclose information when required by law or to protect our legal rights.",
        ],
      },
      {
        h: "Confidentiality & Security",
        ps: [
          "We handle business information with care and use reasonable administrative and technical safeguards to protect it. No method of transmission over the internet is completely secure, and we cannot guarantee absolute security.",
        ],
      },
      {
        h: "Data Retention",
        ps: [
          "We retain service request information for as long as reasonably needed to provide services, maintain business records, and meet applicable requirements. You may request deletion of information you submitted through this website at any time.",
        ],
      },
      {
        h: "Your Choices",
        ps: [
          "You may request access to, correction of, or deletion of the information you submitted by contacting us using the details below.",
        ],
      },
      {
        h: "Changes to This Policy",
        ps: ["We may update this policy from time to time. The current version will always be posted on this page."],
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    intro:
      "These Terms of Use govern your use of this website. By accessing or using this website, you agree to these terms.",
    sections: [
      {
        h: "About the Services",
        ps: [
          "TSM Group CLE provides business compliance and operational support services, including governance and compliance support, workforce and employment verification support, non-DOT drug and alcohol testing services, fingerprinting and background services, notary and document services, and mobile business services. Service availability may vary by service type and location.",
        ],
      },
      {
        h: "No Legal Advice",
        ps: [
          "The content on this website is provided for general business purposes and does not constitute legal, tax, insurance, or other professional advice. Businesses remain responsible for determining and meeting the requirements applicable to their organization and industry.",
        ],
      },
      {
        h: "No Professional Relationship",
        ps: [
          "Submitting a service request or contacting TSM Group CLE through this website does not create a client or professional relationship. Services are provided only after they are requested, reviewed, and confirmed with you directly.",
        ],
      },
      {
        h: "Scheduling, Service Details & Pricing",
        ps: [
          "Service details, scheduling, and pricing are confirmed directly with TSM Group CLE for each request. Information on this website does not constitute an offer or a binding quotation.",
        ],
      },
      {
        h: "Intellectual Property",
        ps: [
          "The content, design, and branding of this website are the property of TSM Group CLE and may not be reproduced without permission.",
        ],
      },
      {
        h: "Third-Party Links",
        ps: [
          "This website may link to third-party tools and services, such as scheduling platforms. We do not control and are not responsible for third-party websites or services.",
        ],
      },
      {
        h: "Limitation of Liability",
        ps: [
          "This website is provided on an \u201cas is\u201d basis without warranties of any kind. To the fullest extent permitted by law, TSM Group CLE is not liable for decisions made or actions taken based on the general information provided on this website.",
        ],
      },
      {
        h: "Governing Law & Changes",
        ps: [
          "These Terms are governed by the laws of the State of Ohio. We may update these Terms from time to time, and the current version will always be posted on this page.",
        ],
      },
    ],
  },
  accessibility: {
    title: "Accessibility",
    intro:
      "TSM Group CLE is committed to making this website accessible to the widest range of visitors, including people with disabilities.",
    sections: [
      {
        h: "Our Commitment",
        ps: [
          "We want every visitor to be able to learn about our services, request a service, and contact us regardless of ability or assistive technology.",
        ],
      },
      {
        h: "Measures We Take",
        ps: [
          "This website is built to follow recognized accessibility practices, including: a clear heading structure; descriptive alternative text for images; color contrast designed for readability; clearly labeled form fields; keyboard-navigable menus and accordions; and support for reduced-motion preferences.",
        ],
      },
      {
        h: "Conformance",
        ps: [
          "We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA and review the website as content and features change.",
        ],
      },
      {
        h: "Feedback",
        ps: [
          "If you encounter an accessibility barrier on this website, please contact us so we can assist you and address the issue.",
        ],
      },
    ],
  },
};

export default function LegalPage({ kind }) {
  const page = CONTENT[kind];
  return (
    <article className="bg-paper pb-24 pt-14 lg:pt-20" data-testid={`legal-page-${kind}`}>
      <div className="container-tsm max-w-3xl">
        <FadeIn>
          <a
            href="/"
            className="inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-golddark transition-colors hover:text-navy"
            data-testid={`legal-back-${kind}`}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </a>
          <h1 className="mt-8 font-serif text-5xl font-semibold tracking-tight text-navy sm:text-6xl" data-testid={`legal-title-${kind}`}>
            {page.title}
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-ink/75">{page.intro}</p>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-navy/40" data-testid={`legal-updated-${kind}`}>
            Last updated: {UPDATED}
          </p>
          <div className="mt-10 space-y-10 border-t border-navy/10 pt-10">
            {page.sections.map((section, i) => (
              <section key={section.h} data-testid={`legal-section-${kind}-${i + 1}`}>
                <h2 className="font-serif text-2xl font-semibold text-navy">{section.h}</h2>
                <div className="mt-3 space-y-3">
                  {section.ps.map((p) => (
                    <p key={p.slice(0, 24)} className="leading-relaxed text-ink/70">
                      {p}
                    </p>
                  ))}
                </div>
              </section>
            ))}
            <p className="leading-relaxed text-ink/70">
              Questions? Contact TSM Group CLE at{" "}
              <a href={SITE.emailHref} className="font-semibold text-navy underline decoration-gold decoration-2 underline-offset-4" data-testid={`legal-email-${kind}`}>
                {SITE.email}
              </a>{" "}
              or{" "}
              <a href={SITE.phoneHref} className="font-semibold text-navy underline decoration-gold decoration-2 underline-offset-4">
                {SITE.phone}
              </a>.
            </p>
          </div>
        </FadeIn>
      </div>
    </article>
  );
}
